import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useProducts } from "@/contexts/ProductContext";
import { Plus, Edit, Trash2, ArchiveRestore, Archive, Minus } from "lucide-react";
import { GramOption } from "@/lib/types";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useState } from "react";
import { useToast } from "@/components/ui/use-toast";

export default function ManageProducts() {
  const { products, addProduct, deleteProductPermanently, updateProduct, archiveProduct, restoreProduct } = useProducts();
  const [open, setOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState({ 
    title: "", 
    description: "", 
    price: "", 
    gst: "5", 
    shippingFee: "0",
    image: "", 
    badge: "" 
  });
  const [gramOptions, setGramOptions] = useState<GramOption[]>([]);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [error, setError] = useState("");
  const { toast } = useToast();

  const handleOpen = () => {
    setForm({ 
      title: "", 
      description: "", 
      price: "", 
      gst: "5", 
      shippingFee: "0",
      image: "", 
      badge: "" 
    });
    setGramOptions([]);
    setError("");
    setEditMode(false);
    setEditId(null);
    setOpen(true);
  };

  const handleEdit = (product: any) => {
    setForm({
      title: product.title,
      description: product.description,
      price: product.price.toString(),
      gst: (product.gst || 0).toString(),
      shippingFee: (product.shippingFee || 0).toString(),
      image: product.image,
      badge: product.badge || ""
    });
    setGramOptions(product.gramOptions || []);
    setEditMode(true);
    setEditId(product.id);
    setError("");
    setOpen(true);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    if (type === "file" && name === "image") {
      const file = (e.target as HTMLInputElement).files?.[0] || null;
      setImageFile(file);
    } else {
      setForm({ ...form, [name]: value });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.title || !form.description || !form.price) {
      setError("All fields except badge and image are required.");
      return;
    }
    let imageData = form.image;
    if (imageFile) {
      if (!/\.(jpg|jpeg|png)$/i.test(imageFile.name)) {
        setError("Image must be a .jpg or .png file.");
        return;
      }
      imageData = await toBase64(imageFile);
    }
    try {
      if (editMode && editId) {
        await updateProduct(editId, {
          title: form.title,
          description: form.description,
          price: parseFloat(form.price),
          gst: parseFloat(form.gst) || 0,
          shippingFee: parseFloat(form.shippingFee) || 0,
          image: imageData,
          badge: form.badge || undefined,
          gramOptions: gramOptions.length > 0 ? gramOptions : undefined,
        });
        setOpen(false);
        setImageFile(null);
      } else {
        const success = await addProduct({
          title: form.title,
          description: form.description,
          price: parseFloat(form.price),
          gst: parseFloat(form.gst) || 0,
          shippingFee: parseFloat(form.shippingFee) || 0,
          image: imageData,
          badge: form.badge || undefined,
          gramOptions: gramOptions.length > 0 ? gramOptions : undefined,
        });
        if (success) {
          setOpen(false);
          setImageFile(null);
        }
      }
    } catch (err: any) {
      toast({
        title: 'Error',
        description: err.message || 'Failed to add product. Check your connection or permissions.',
        variant: 'destructive',
      });
    }
  };

  function toBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = error => reject(error);
    });
  }

  const handleDelete = (id: string) => {
    if (window.confirm("Are you sure you want to delete this product?")) {
      deleteProductPermanently(id);
    }
  };

  const handleArchive = (id: string) => {
    if (window.confirm("Archive this product? It will show as out of stock.")) {
      archiveProduct(id);
    }
  };

  const handleRestore = (id: string) => {
    if (window.confirm("Restore this product to active status?")) {
      restoreProduct(id);
    }
  };

  const addGramOption = () => {
    const newOption: GramOption = {
      id: Date.now().toString(),
      weight: 0,
      price: 0,
      label: ""
    };
    setGramOptions([...gramOptions, newOption]);
  };

  const updateGramOption = (index: number, field: keyof GramOption, value: string | number) => {
    const updatedOptions = [...gramOptions];
    updatedOptions[index] = { ...updatedOptions[index], [field]: value };
    setGramOptions(updatedOptions);
  };

  const removeGramOption = (index: number) => {
    setGramOptions(gramOptions.filter((_, i) => i !== index));
  };

  return (
    <div className="w-full space-y-4 sm:space-y-6 p-4 sm:p-6">
      <div className="flex flex-col space-y-4 md:space-y-0 md:flex-row md:justify-between md:items-start">
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl sm:text-3xl font-bold">Manage Products</h1>
          <p className="text-muted-foreground text-sm sm:text-base mt-1">
            Add, edit, or remove products from your store
          </p>
        </div>
        <div className="flex-shrink-0 w-full md:w-auto mt-2 md:mt-0">
          <Button onClick={handleOpen} className="w-full md:w-auto btn-touch">
            <Plus className="mr-2 h-4 w-4 flex-shrink-0" />
            <span>Add Product</span>
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg sm:text-xl">Products</CardTitle>
          <CardDescription className="text-sm">
            Manage your product inventory and details
          </CardDescription>
        </CardHeader>
        <CardContent className="p-4 sm:p-6">
          {/* Mobile/Tablet Card View */}
          <div className="block lg:hidden space-y-4">
            {products.map((product) => (
              <Card key={product.id} className="p-4 transition-all duration-200 hover:shadow-md">
                <div className="flex items-start gap-3 sm:gap-4">
                  <img
                    src={product.image}
                    alt={product.title}
                    className="h-16 w-16 sm:h-20 sm:w-20 rounded-md object-cover flex-shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-sm sm:text-base truncate">{product.title}</h3>
                    <p className="text-lg sm:text-xl font-bold text-green-600 mt-1">₹{product.price.toFixed(2)}</p>
                    <div className="mt-2">
                      {product.status === 'archived' ? (
                        <span className="px-2 py-1 text-xs rounded-full bg-gray-200 text-gray-700 dark:bg-gray-800 dark:text-gray-200">
                          Out of Stock
                        </span>
                      ) : (
                        <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                          Active
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-2 mt-3">
                      <Button variant="outline" size="sm" onClick={() => handleEdit(product)} className="btn-touch text-xs sm:text-sm">
                        <Edit className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                        <span>Edit</span>
                      </Button>
                      {product.status === 'archived' ? (
                        <Button variant="outline" size="sm" className="text-green-600 hover:bg-green-50 hover:text-green-700 btn-touch text-xs sm:text-sm" onClick={() => handleRestore(product.id)}>
                          <ArchiveRestore className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                          <span>Restore</span>
                        </Button>
                      ) : (
                        <Button variant="outline" size="sm" className="text-yellow-600 hover:bg-yellow-50 hover:text-yellow-700 btn-touch text-xs sm:text-sm" onClick={() => handleArchive(product.id)}>
                          <Archive className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                          <span>Archive</span>
                        </Button>
                      )}
                      <Button variant="outline" size="sm" className="text-red-600 hover:bg-red-50 hover:text-red-700 btn-touch" onClick={() => handleDelete(product.id)}>
                        <Trash2 className="h-3 w-3 sm:h-4 sm:w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
          
          {/* Desktop Table View */}
          <div className="hidden lg:block overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Product</TableHead>
                  <TableHead>Price</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {products.map((product) => (
                  <TableRow key={product.id}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-3">
                        <img
                          src={product.image}
                          alt={product.title}
                          className="h-10 w-10 lg:h-12 lg:w-12 rounded-md object-cover"
                        />
                        <span className="text-sm lg:text-base">{product.title}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm lg:text-base">₹{product.price.toFixed(2)}</TableCell>
                    <TableCell>
                      {product.status === 'archived' ? (
                        <span className="px-3 py-1 text-xs md:text-sm rounded-full bg-gray-200 text-gray-700 dark:bg-gray-800 dark:text-gray-200">
                          Out of Stock
                        </span>
                      ) : (
                        <span className="px-3 py-1 text-xs md:text-sm rounded-full bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                          Active
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button variant="outline" size="icon" onClick={() => handleEdit(product)}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        {product.status === 'archived' ? (
                          <Button variant="outline" size="icon" className="text-green-600 hover:bg-green-50 hover:text-green-700" onClick={() => handleRestore(product.id)} title="Restore">
                            <ArchiveRestore className="h-4 w-4" />
                          </Button>
                        ) : (
                          <Button variant="outline" size="icon" className="text-yellow-600 hover:bg-yellow-50 hover:text-yellow-700" onClick={() => handleArchive(product.id)} title="Archive">
                            <Archive className="h-4 w-4" />
                          </Button>
                        )}
                        <Button variant="outline" size="icon" className="text-red-600 hover:bg-red-50 hover:text-red-700" onClick={() => handleDelete(product.id)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="w-full h-full max-w-full max-h-full sm:max-w-lg sm:h-auto sm:max-h-[90vh] md:max-w-2xl lg:max-w-4xl flex flex-col m-0 sm:m-4 rounded-none sm:rounded-lg">
          {/* Fixed Header */}
          <DialogHeader className="flex-shrink-0 pb-3 border-b bg-white px-4 sm:px-6">
            <DialogTitle className="text-xl font-semibold">{editMode ? 'Edit Product' : 'Add Product'}</DialogTitle>
          </DialogHeader>
          
          {/* Scrollable Form */}
          <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4">
            <form onSubmit={handleSubmit} className="space-y-4 pb-4" id="product-form">
            <div>
              <label className="block text-sm font-medium mb-2">Product Title</label>
              <input
                name="title"
                value={form.title}
                onChange={handleChange}
                placeholder="Enter product title"
                className="w-full border rounded px-3 py-3 text-base form-input-touch"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Description</label>
              <textarea
                name="description"
                value={form.description}
                onChange={handleChange}
                placeholder="Enter product description"
                className="w-full border rounded px-3 py-3 text-base min-h-[100px] form-input-touch"
                required
              />
            </div>
            <div className="admin-form-grid">
              <div>
                <label className="block text-sm font-medium mb-2">Price (₹)</label>
                <input
                  name="price"
                  value={form.price}
                  onChange={handleChange}
                  placeholder="0.00"
                  type="number"
                  min="0"
                  step="0.01"
                  className="w-full border rounded px-3 py-3 text-base form-input-touch"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">GST (%)</label>
                <input
                  name="gst"
                  value={form.gst}
                  onChange={handleChange}
                  placeholder="0"
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  className="w-full border rounded px-3 py-3 text-base form-input-touch"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Shipping Fee (₹)</label>
                <input
                  name="shippingFee"
                  value={form.shippingFee}
                  onChange={handleChange}
                  placeholder="0.00"
                  type="number"
                  min="0"
                  step="0.01"
                  className="w-full border rounded px-3 py-3 text-base form-input-touch"
                  required
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Product Image</label>
              <input
                name="image"
                type="file"
                accept=".jpg,.jpeg,.png"
                onChange={handleChange}
                className="w-full border rounded px-3 py-3 text-base form-input-touch"
                required={!editMode}
              />
              {editMode && !imageFile && form.image && (
                <div className="mt-2">
                  <img src={form.image} alt="Current" className="h-20 w-20 md:h-24 md:w-24 object-cover rounded" />
                  <span className="text-xs text-gray-500 ml-2">Current image</span>
                </div>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Badge (Optional)</label>
              <input
                name="badge"
                value={form.badge}
                onChange={handleChange}
                placeholder="Enter badge text (e.g., New, Sale, etc.)"
                className="w-full border rounded px-3 py-3 text-base form-input-touch"
              />
            </div>
            
            {/* Gram Options Section */}
            <div className="space-y-4">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
                <label className="block text-sm font-medium">Weight Options (Optional)</label>
                <Button type="button" variant="outline" size="sm" onClick={addGramOption} className="w-full sm:w-auto btn-touch">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Weight Option
                </Button>
              </div>
              {gramOptions.map((option, index) => (
                <div key={option.id} className="p-4 border rounded-lg space-y-4 sm:space-y-0">
                  <div className="admin-form-grid">
                    <div>
                      <label className="block text-sm font-medium mb-2">Weight (g)</label>
                      <input
                        type="number"
                        placeholder="Weight (g)"
                        value={option.weight}
                        onChange={(e) => updateGramOption(index, 'weight', parseFloat(e.target.value) || 0)}
                        className="w-full border rounded px-3 py-3 text-base form-input-touch"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Price (₹)</label>
                      <input
                        type="number"
                        placeholder="Price (₹)"
                        value={option.price}
                        onChange={(e) => updateGramOption(index, 'price', parseFloat(e.target.value) || 0)}
                        className="w-full border rounded px-3 py-3 text-base form-input-touch"
                        step="0.01"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Label (optional)</label>
                      <input
                        type="text"
                        placeholder="Label (optional)"
                        value={option.label || ''}
                        onChange={(e) => updateGramOption(index, 'label', e.target.value)}
                        className="w-full border rounded px-3 py-3 text-base form-input-touch"
                      />
                    </div>
                  </div>
                  <div className="flex justify-end mt-4">
                    <Button type="button" variant="outline" size="sm" onClick={() => removeGramOption(index)} className="w-full sm:w-auto btn-touch text-red-600 hover:bg-red-50">
                      <Minus className="h-4 w-4 mr-2" />
                      Remove Option
                    </Button>
                  </div>
                </div>
              ))}
              {gramOptions.length === 0 && (
                <p className="text-sm text-gray-500">No weight options added. Product will use the base price above.</p>
              )}
            </div>
            {error && <div className="text-red-500 text-sm">{error}</div>}
            </form>
          </div>
          
          {/* Fixed Footer */}
          <DialogFooter className="flex-shrink-0 border-t pt-4 bg-white px-4 sm:px-6">
            <div className="flex flex-col-reverse sm:flex-row gap-3 w-full sm:w-auto sm:justify-end">
              <Button type="button" variant="outline" onClick={() => setOpen(false)} className="w-full sm:w-auto btn-touch">
                Cancel
              </Button>
              <Button type="submit" form="product-form" className="w-full sm:w-auto btn-touch">
                {editMode ? 'Save Changes' : 'Add Product'}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
